import type { InvoiceConcept, Movement, MovementKind } from '../model/period';

const cents = (value: number) => Math.round(value * 100) / 100;

export const PENDING_REVIEW_TEXT = 'Lo revisamos al armar tu declaración';

export const AI_UNDECIDED_CODE = 'sin_decidir';

export const awaitsUser = (concept: InvoiceConcept) => concept.deduction?.code === AI_UNDECIDED_CODE;

export const reviewedByAccountant = (concept: InvoiceConcept) => concept.deduction?.source === 'contador';

export const conceptKey = (movementId: string, index: number) => `${movementId}:${index}`;

const withIva = (concept: InvoiceConcept) => (concept.amount ?? 0) + (concept.iva ?? 0);

export const conceptTotal = (movement: Movement, concept: InvoiceConcept) => {
	const total = (movement.invoice.concepts ?? []).reduce((sum, item) => sum + withIva(item), 0);
	return total > 0 ? cents((withIva(concept) / total) * movement.amount) : 0;
};

export const conceptIva = (movement: Movement, concept: InvoiceConcept) => {
	const total = (movement.invoice.concepts ?? []).reduce((sum, item) => sum + withIva(item), 0);
	return total > 0 ? cents(((concept.iva ?? 0) / total) * movement.amount) : 0;
};

export type MovementEntry = {
	key: string;
	movement: Movement;
	kind: MovementKind;
	amount: number;
	partOf?: number;
	split?: { deducible: number; noDeducible: number };
	summary: string;
	reason?: string;
	pending?: boolean;
	overridable: boolean;
	markedByUser: boolean;
	adjustments?: Movement[];
	original?: number;
	exceeds?: boolean;
};

export const wholeEntry = (movement: Movement): MovementEntry => ({
	key: movement.id,
	movement,
	kind: movement.kind,
	amount: movement.amount,
	summary: movement.concept,
	overridable: false,
	markedByUser: false,
});

export const entriesOf = (movement: Movement): MovementEntry[] => {
	const concepts = movement.invoice.concepts ?? [];
	const measurable = concepts.every((concept) => concept.amount !== undefined);
	if (movement.kind === 'ingreso' || !measurable || !concepts.some((concept) => concept.deduction)) return [wholeEntry(movement)];
	const sides = [true, false].map((deductible) => concepts.filter((concept) => concept.deduction?.deductible === deductible));
	const partial = sides.every((side) => side.length > 0);
	return sides.flatMap((side, index): MovementEntry[] => {
		if (side.length === 0) return [];
		const deductible = index === 0;
		const explained = deductible ? undefined : side.find((concept) => concept.deduction?.reason)?.deduction;
		return [
			{
				key: `${movement.id}:${deductible ? 'deducible' : 'no_deducible'}`,
				movement,
				kind: deductible ? 'deducible' : 'no_deducible',
				amount: partial ? cents(side.reduce((sum, concept) => sum + conceptTotal(movement, concept), 0)) : movement.amount,
				partOf: partial ? movement.amount : undefined,
				summary: partial ? (side.length > 1 ? `${side[0].description} y ${side.length - 1} más` : side[0].description) : movement.concept,
				reason: explained?.reason,
				pending: explained?.pending,
				overridable: !deductible && side.some((concept) => concept.deduction?.overridable),
				markedByUser: deductible && side.some((concept) => concept.deduction?.markedByUser),
			},
		];
	});
};

export const deductionSplit = (movement: Movement) => {
	const entries = entriesOf(movement);
	const amountOf = (kind: MovementKind) => entries.find((entry) => entry.kind === kind)?.amount ?? 0;
	return { deducible: amountOf('deducible'), noDeducible: amountOf('no_deducible'), partial: entries.length > 1 };
};
